import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { daysUntil, formatFullDate, formatRelativeTime } from "@/lib/format";
import { downloadCsv, type CsvColumn } from "@/lib/csv";
import type { Customer, RenewalPush, RenewalPushResult } from "@/pages/admin/customers/hooks/useCustomers";

interface CustomerHealthBoardProps {
  customers: Customer[];
  loading: boolean;
  lastRemindedAt: Record<string, string>;
  recentPushes: RenewalPush[];
  onPushReminders: (windowDays: number, userId?: string) => Promise<RenewalPushResult>;
}

// 推送可选到期窗口：与健康看板顶部「30/60/90 天内到期」统计口径完全一致。
const PUSH_WINDOWS = [30, 60, 90];
const DAY_MS = 86400000;
// 提醒去重周期：与续期预测「30 天内到期」口径一致，30 天内推过即视为本周期已提醒。
const REMINDER_CYCLE_DAYS = 30;
// 单独推送固定按「30 天内到期」窗口补推一次，与续期预测面板的口径保持一致。
const SINGLE_PUSH_WINDOW = 30;

type ReminderState = "reminded" | "stale" | "none" | "guest";

// 单客户提醒状态：本周期已提醒 / 上周期提醒过 / 从未提醒 / 游客（不参与推送），
// 让看板上每一行都能一眼看出该客户是否还在去重保护期内。
function reminderState(
  lastRemindedAt: Record<string, string>,
  userId: string | null,
): { state: ReminderState; last: string | null } {
  if (!userId) return { state: "guest", last: null };
  const last = lastRemindedAt[userId] ?? null;
  if (!last) return { state: "none", last: null };
  const daysSince = (Date.now() - new Date(last).getTime()) / DAY_MS;
  return { state: daysSince >= 0 && daysSince < REMINDER_CYCLE_DAYS ? "reminded" : "stale", last };
}

function withinWindow(expiresAt: string | null, windowDays: number): boolean {
  if (!expiresAt) return false;
  const days = daysUntil(expiresAt);
  return days >= 0 && days <= windowDays;
}

interface RenewalRow {
  customer: Customer;
  expiringCount: number;
  within30: number;
  next: string | null;
  nextDays: number | null;
}

function scrollToList() {
  document.getElementById("customer-list")?.scrollIntoView({ behavior: "smooth" });
}

// 客户健康度看板：续期预测（按 30/60/90 天窗口统计即将到期许可证）+ 流失预警（长期无活跃客户）。
// 纯前端从已加载的客户数据聚合，复用 useCustomers 的许可证 / 风险标记结果。
export default function CustomerHealthBoard({
  customers,
  loading,
  lastRemindedAt,
  recentPushes,
  onPushReminders,
}: CustomerHealthBoardProps) {
  const [exporting, setExporting] = useState(false);
  const [confirmPush, setConfirmPush] = useState(false);
  const [pushing, setPushing] = useState(false);
  // 单独推送的目标客户：点某一行「单独推送」时打开对应的确认弹窗。
  const [singleTarget, setSingleTarget] = useState<RenewalRow | null>(null);
  // 「最近推送记录」里展开查看覆盖客户的推送 id（按批次时间戳）。
  const [expandedPush, setExpandedPush] = useState<string | null>(null);
  const counts = useMemo(() => {
    let c30 = 0;
    let c60 = 0;
    let c90 = 0;
    customers.forEach((customer) => {
      customer.licenses.forEach((license) => {
        if (license.status !== "active" || !license.expires_at) return;
        const days = daysUntil(license.expires_at);
        if (days < 0) return;
        if (days <= 30) c30 += 1;
        if (days <= 60) c60 += 1;
        if (days <= 90) c90 += 1;
      });
    });
    return { c30, c60, c90 };
  }, [customers]);

  const renewalRows = useMemo<RenewalRow[]>(() => {
    return customers
      .map((customer) => {
        const expiring = customer.licenses.filter((license) => license.status === "active" && license.expires_at);
        const within30 = expiring.filter((license) => {
          const days = daysUntil(license.expires_at as string);
          return days >= 0 && days <= 30;
        });
        const next =
          expiring.map((license) => license.expires_at as string).sort((a, b) => daysUntil(a) - daysUntil(b))[0] ?? null;
        return {
          customer,
          expiringCount: expiring.length,
          within30: within30.length,
          next,
          nextDays: next ? daysUntil(next) : null,
        };
      })
      .filter((row) => row.within30 > 0)
      .sort((a, b) => (a.nextDays ?? 0) - (b.nextDays ?? 0));
  }, [customers]);

  // 可收到站内通知的客户（已注册、有 user_id），游客申请不在推送范围内。
  // 推送窗口（30/60/90 天，与看板统计口径一致）：每个窗口下统计可推送客户、涉及张数与去重跳过数。
  const [pushWindow, setPushWindow] = useState(30);
  const windowSummary = useMemo(() => {
    const now = Date.now();
    const summary: Record<number, { customers: number; licenses: number; skipped: number }> = {};
    PUSH_WINDOWS.forEach((windowDays) => {
      let customerCount = 0;
      let licenseCount = 0;
      let skipped = 0;
      customers.forEach((customer) => {
        if (!customer.userId) return;
        const count = customer.licenses.filter(
          (license) => license.status === "active" && withinWindow(license.expires_at, windowDays),
        ).length;
        if (count === 0) return;
        customerCount += 1;
        licenseCount += count;
        const last = lastRemindedAt[customer.userId];
        if (last) {
          const daysSince = (now - new Date(last).getTime()) / DAY_MS;
          if (daysSince >= 0 && daysSince < windowDays) skipped += 1;
        }
      });
      summary[windowDays] = { customers: customerCount, licenses: licenseCount, skipped };
    });
    return summary;
  }, [customers, lastRemindedAt]);

  const currentWindow = windowSummary[pushWindow] ?? { customers: 0, licenses: 0, skipped: 0 };
  // 去重后的实际可推送人数：命中客户中扣掉本周期内已提醒过的。
  const willPush = Math.max(0, currentWindow.customers - currentWindow.skipped);
  const canPush = PUSH_WINDOWS.some((windowDays) => (windowSummary[windowDays]?.customers ?? 0) > 0);

  const openPush = () => {
    const first = PUSH_WINDOWS.find((windowDays) => (windowSummary[windowDays]?.customers ?? 0) > 0) ?? 30;
    setPushWindow(first);
    setConfirmPush(true);
  };

  const handleConfirmPush = async () => {
    if (pushing) return;
    setPushing(true);
    try {
      await onPushReminders(pushWindow);
    } finally {
      setPushing(false);
      setConfirmPush(false);
    }
  };

  // 单独推送：只针对某一位客户补推一次，服务端复用同一套周期去重规则。
  const handleConfirmSinglePush = async () => {
    if (!singleTarget || pushing) return;
    setPushing(true);
    try {
      await onPushReminders(SINGLE_PUSH_WINDOW, singleTarget.customer.userId ?? undefined);
    } finally {
      setPushing(false);
      setSingleTarget(null);
    }
  };

  const inactiveCustomers = useMemo(
    () => customers.filter((customer) => customer.riskFlags.some((flag) => flag.key === "inactive")),
    [customers],
  );

  // 续期跟进清单：把 30 天内到期的客户导成 CSV，交给客服按剩余天数定向提醒续期。
  const handleExport = () => {
    if (renewalRows.length === 0) return;
    const columns: CsvColumn<RenewalRow>[] = [
      { key: "customer", label: "客户", value: (row) => row.customer.name },
      { key: "email", label: "联系方式", value: (row) => row.customer.email ?? "" },
      { key: "owner", label: "跟进负责人", value: (row) => row.customer.followUpAssignee ?? "未指派" },
      { key: "count", label: "30 天内到期张数", value: (row) => row.within30 },
      { key: "expiring", label: "在管许可证数", value: (row) => row.expiringCount },
      { key: "next", label: "最近到期", value: (row) => (row.next ? formatFullDate(row.next) : "") },
      { key: "days", label: "剩余天数", value: (row) => row.nextDays ?? "" },
    ];
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`续期跟进清单_${stamp}.csv`, columns, renewalRows);
    setExporting(true);
    window.setTimeout(() => setExporting(false), 2000);
  };
  const swappingCount = useMemo(
    () => customers.filter((customer) => customer.riskFlags.some((flag) => flag.key === "swapping")).length,
    [customers],
  );

  const statCards = [
    { label: "30 天内到期", value: counts.c30, icon: "ri-alarm-warning-line", tone: "accent" },
    { label: "60 天内到期", value: counts.c60, icon: "ri-timer-line", tone: "secondary" },
    { label: "90 天内到期", value: counts.c90, icon: "ri-calendar-line", tone: "primary" },
    { label: "流失预警客户", value: inactiveCustomers.length, icon: "ri-user-unfollow-line", tone: "accent" },
  ];

  const toneClass: Record<string, string> = {
    primary: "bg-primary-100 text-primary-700",
    accent: "bg-accent-100 text-accent-700",
    secondary: "bg-secondary-100 text-secondary-900",
  };

  return (
    <section className="rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-700">
            <i className="ri-heart-pulse-line" />
          </span>
          <div>
            <h2 className="font-heading text-sm font-semibold text-foreground-950">客户健康度看板</h2>
            <p className="text-[11px] text-foreground-500">
              续期预测（即将到期许可证）与流失预警（长期无活跃客户），提前锁定需要跟进的客户。
            </p>
          </div>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-background-100 px-3 py-1 text-[11px] font-medium text-foreground-700">
          <span className="flex h-3 w-3 items-center justify-center">
            <i className="ri-group-line" />
          </span>
          覆盖 {customers.length} 位客户
        </span>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((card) => (
          <div key={card.label} className="rounded-md border border-background-200 bg-background-100 p-3.5">
            <div className="flex items-center justify-between">
              <span className="text-xs text-foreground-600">{card.label}</span>
              <span className={`flex h-7 w-7 items-center justify-center rounded-md ${toneClass[card.tone]}`}>
                <i className={`${card.icon} text-sm`} />
              </span>
            </div>
            <p className="mt-2 font-heading text-xl font-semibold text-foreground-950">
              {loading ? "--" : card.value}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        {/* 续期预测 */}
        <div className="rounded-md border border-background-200 bg-background-50 p-4">
          <p className="flex items-center justify-between gap-2 text-sm font-medium text-foreground-950">
            <span className="flex items-center gap-2">
              <span className="flex h-4 w-4 items-center justify-center text-accent-600">
                <i className="ri-refresh-line" />
              </span>
              续期预测 · 30 天内到期
            </span>
            <span className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-normal text-foreground-500">{renewalRows.length} 位客户</span>
              <button
                type="button"
                disabled={renewalRows.length === 0}
                onClick={handleExport}
                title="把 30 天内到期的客户导成跟进清单，交给客服定向提醒续期"
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-primary-300 bg-primary-50 px-2.5 py-1 text-[11px] font-medium text-primary-700 transition-colors hover:bg-primary-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className={exporting ? "ri-check-line" : "ri-download-2-line"} />
                </span>
                {exporting ? "已导出" : "导出跟进清单"}
              </button>
              <button
                type="button"
                disabled={!canPush}
                onClick={openPush}
                title="一键向即将到期客户推送续期提醒（站内通知 + 邮件），可选 30/60/90 天窗口并自动去重"
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-accent-300 bg-accent-50 px-2.5 py-1 text-[11px] font-medium text-accent-800 transition-colors hover:bg-accent-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className="ri-notification-3-line" />
                </span>
                推送续期提醒
              </button>
            </span>
          </p>
          {loading ? (
            <div className="mt-3 flex flex-col gap-2">
              {[0, 1, 2].map((item) => (
                <div key={item} className="h-10 rounded-md bg-background-100" />
              ))}
            </div>
          ) : renewalRows.length === 0 ? (
            <p className="mt-3 rounded-md border border-background-200 bg-background-100 px-3 py-6 text-center text-xs text-foreground-500">
              近 30 天内没有即将到期的许可证，续期压力较小。
            </p>
          ) : (
            <ul className="mt-3 flex flex-col gap-2">
              {renewalRows.slice(0, 6).map((row) => {
                const reminder = reminderState(lastRemindedAt, row.customer.userId);
                const isReminded = reminder.state === "reminded";
                const reminderLabel =
                  reminder.state === "guest"
                    ? "游客申请，不参与推送"
                    : reminder.last
                      ? `上次提醒 ${formatRelativeTime(reminder.last)}`
                      : "本周期尚未提醒";
                return (
                  <li
                    key={row.customer.key}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2.5"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-xs font-medium text-foreground-900">{row.customer.name}</p>
                      <p className="mt-0.5 text-[11px] text-foreground-500">
                        {row.within30} 张 30 天内到期
                        {row.next ? ` · 最近 ${formatFullDate(row.next)}（${row.nextDays} 天后）` : ""}
                      </p>
                      <p className="mt-0.5 flex items-center gap-1 text-[11px] text-foreground-500">
                        <span className="flex h-3 w-3 items-center justify-center">
                          <i
                            className={`${
                              reminder.state === "guest"
                                ? "ri-user-forbid-line text-foreground-400"
                                : isReminded
                                  ? "ri-checkbox-circle-line text-accent-600"
                                  : "ri-notification-3-line text-foreground-400"
                            }`}
                          />
                        </span>
                        {reminderLabel}
                      </p>
                    </div>
                    <span className="flex shrink-0 flex-wrap items-center gap-1.5">
                      <button
                        type="button"
                        disabled={reminder.state === "guest" || isReminded}
                        onClick={() => setSingleTarget(row)}
                        title={
                          reminder.state === "guest"
                            ? "游客申请不在推送范围内"
                            : isReminded
                              ? "该客户本周期内已提醒，无需重复推送"
                              : "只向该客户补推一次续期提醒（服务端仍会按周期去重）"
                        }
                        className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border border-accent-300 bg-accent-50 px-2.5 py-1 text-[11px] font-medium text-accent-800 transition-colors hover:bg-accent-100 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <span className="flex h-3 w-3 items-center justify-center">
                          <i className="ri-send-plane-line" />
                        </span>
                        单独推送
                      </button>
                      <span
                        className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-medium ${
                          isReminded
                            ? "bg-secondary-100 text-secondary-900"
                            : reminder.state === "guest"
                              ? "bg-background-200 text-foreground-500"
                              : "bg-background-200 text-foreground-700"
                        }`}
                      >
                        <span className="flex h-3 w-3 items-center justify-center">
                          <i
                            className={
                              isReminded
                                ? "ri-mail-check-line"
                                : reminder.state === "guest"
                                  ? "ri-user-forbid-line"
                                  : "ri-mail-send-line"
                            }
                          />
                        </span>
                        {reminder.state === "guest"
                          ? "不参与推送"
                          : isReminded
                            ? "本周期已提醒"
                            : "本周期待提醒"}
                      </span>
                      <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-accent-100 px-2.5 py-1 text-[11px] font-medium text-accent-800">
                        <span className="flex h-3 w-3 items-center justify-center">
                          <i className="ri-time-line" />
                        </span>
                        {row.nextDays} 天后
                      </span>
                    </span>
                  </li>
                );
              })}
              {renewalRows.length > 6 && (
                <li className="text-center text-[11px] text-foreground-400">另有 {renewalRows.length - 6} 位客户…</li>
              )}
            </ul>
          )}

          {/* 最近推送记录：展示近几次推送的时间、人数与邮件成败，让推送历史一眼可回看。 */}
          <div className="mt-3 rounded-md border border-background-200 bg-background-100 p-3">
            <p className="flex items-center gap-2 text-[11px] font-medium text-foreground-700">
              <span className="flex h-3.5 w-3.5 items-center justify-center text-accent-600">
                <i className="ri-history-line" />
              </span>
              最近推送记录
              {recentPushes.length > 0 && <span className="font-normal text-foreground-400">近 {recentPushes.length} 次</span>}
            </p>
            {recentPushes.length === 0 ? (
              <p className="mt-2 rounded-md bg-background-50 px-3 py-4 text-center text-[11px] text-foreground-500">
                还没有推送记录，点上方「推送续期提醒」后会在这里留下每一次的留痕。
              </p>
            ) : (
              <ul className="mt-2 flex flex-col gap-1.5">
                {recentPushes.slice(0, 5).map((push) => {
                  const isOpen = expandedPush === push.id;
                  return (
                    <li key={push.id} className="rounded-md bg-background-50 px-3 py-2">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="flex min-w-0 items-center gap-1.5 text-[11px] text-foreground-600">
                          <span className="flex h-3 w-3 items-center justify-center text-foreground-400">
                            <i className="ri-time-line" />
                          </span>
                          {formatRelativeTime(push.createdAt)}
                          {push.createdByName && <span className="text-foreground-400">· {push.createdByName}</span>}
                        </span>
                        <span className="flex shrink-0 flex-wrap items-center gap-1.5">
                          <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-accent-100 px-2 py-0.5 text-[10px] font-medium text-accent-800">
                            <span className="flex h-3 w-3 items-center justify-center">
                              <i className="ri-user-line" />
                            </span>
                            {push.count} 人
                          </span>
                          <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-background-200 px-2 py-0.5 text-[10px] font-medium text-foreground-700">
                            {push.windowDays} 天窗口
                          </span>
                          <span
                            className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-medium ${
                              push.emailFailed > 0 ? "bg-secondary-100 text-secondary-900" : "bg-primary-100 text-primary-700"
                            }`}
                          >
                            <span className="flex h-3 w-3 items-center justify-center">
                              <i className={push.emailFailed > 0 ? "ri-mail-close-line" : "ri-mail-check-line"} />
                            </span>
                            邮件 {push.emailSent}/{push.emailSent + push.emailFailed}
                          </span>
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setExpandedPush(isOpen ? null : push.id)}
                        className="mt-1.5 inline-flex cursor-pointer items-center gap-1 text-[10px] font-medium text-primary-700 transition-colors hover:text-primary-800"
                      >
                        <span className="flex h-3 w-3 items-center justify-center">
                          <i className={isOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
                        </span>
                        查看覆盖客户（{push.covers.length}）
                      </button>
                      {isOpen && (
                        <div className="mt-1.5 flex flex-wrap gap-1.5 rounded-md border border-background-200 bg-background-100 p-2">
                          {push.covers.length === 0 ? (
                            <span className="text-[10px] text-foreground-400">本次推送无客户明细</span>
                          ) : (
                            push.covers.map((cover) => (
                              <span
                                key={cover.userId}
                                className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-background-50 px-2 py-0.5 text-[10px] text-foreground-700"
                              >
                                <span className="flex h-3 w-3 items-center justify-center text-foreground-400">
                                  <i className="ri-user-line" />
                                </span>
                                {cover.name}
                              </span>
                            ))
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
                {recentPushes.length > 5 && (
                  <li className="text-center text-[10px] text-foreground-400">另有 {recentPushes.length - 5} 次推送…</li>
                )}
              </ul>
            )}
          </div>
        </div>

        {/* 流失预警 */}
        <div className="rounded-md border border-background-200 bg-background-50 p-4">
          <p className="flex items-center justify-between gap-2 text-sm font-medium text-foreground-950">
            <span className="flex items-center gap-2">
              <span className="flex h-4 w-4 items-center justify-center text-accent-600">
                <i className="ri-user-unfollow-line" />
              </span>
              流失预警 · 长期无活跃
            </span>
            <span className="text-xs font-normal text-foreground-500">
              {inactiveCustomers.length} 位 · 频繁换机 {swappingCount} 位
            </span>
          </p>
          {loading ? (
            <div className="mt-3 flex flex-col gap-2">
              {[0, 1, 2].map((item) => (
                <div key={item} className="h-10 rounded-md bg-background-100" />
              ))}
            </div>
          ) : inactiveCustomers.length === 0 ? (
            <p className="mt-3 rounded-md border border-background-200 bg-background-100 px-3 py-6 text-center text-xs text-foreground-500">
              暂无长期无活跃的客户，客户活跃度良好。
            </p>
          ) : (
            <>
              <ul className="mt-3 flex flex-col gap-2">
                {inactiveCustomers.slice(0, 6).map((customer) => {
                  const flag = customer.riskFlags.find((item) => item.key === "inactive");
                  return (
                    <li
                      key={customer.key}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2.5"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-xs font-medium text-foreground-900">{customer.name}</p>
                        <p className="mt-0.5 truncate text-[11px] text-foreground-500">
                          {flag?.detail ?? "长期无活跃"}
                          {customer.lastActivity
                            ? ` · 最近操作 ${formatRelativeTime(new Date(customer.lastActivity))}`
                            : ""}
                        </p>
                      </div>
                      <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-accent-100 px-2.5 py-1 text-[11px] font-medium text-accent-800">
                        <span className="flex h-3 w-3 items-center justify-center">
                          <i className="ri-zzz-line" />
                        </span>
                        长期无活跃
                      </span>
                    </li>
                  );
                })}
                {inactiveCustomers.length > 6 && (
                  <li className="text-center text-[11px] text-foreground-400">
                    另有 {inactiveCustomers.length - 6} 位客户…
                  </li>
                )}
              </ul>
              <div className="mt-3">
                <Link
                  to="/admin/customers?filter=risky"
                  onClick={scrollToList}
                  className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-accent-300 bg-accent-50 px-3 py-2 text-xs font-medium text-accent-800 transition-colors hover:bg-accent-100"
                >
                  <span className="flex h-3.5 w-3.5 items-center justify-center">
                    <i className="ri-arrow-down-circle-line" />
                  </span>
                  查看全部风险客户
                </Link>
              </div>
            </>
          )}
        </div>
      </div>

      {confirmPush && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-lg border border-background-200 bg-background-50 p-5">
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className="ri-notification-3-line" />
              </span>
              <h3 className="font-heading text-sm font-semibold text-foreground-950">推送续期提醒</h3>
            </div>

            <div className="mt-4 flex items-center justify-between gap-2">
              <span className="text-[11px] font-medium text-foreground-600">到期窗口</span>
              <div className="inline-flex rounded-full bg-background-100 p-1">
                {PUSH_WINDOWS.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setPushWindow(option)}
                    className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1 text-[11px] font-medium transition-colors ${
                      pushWindow === option
                        ? "bg-accent-600 text-background-50"
                        : "text-foreground-600 hover:text-foreground-950"
                    }`}
                  >
                    {option} 天
                  </button>
                ))}
              </div>
            </div>

            <p className="mt-4 text-xs leading-relaxed text-foreground-600">
              本次将推送 <strong className="text-foreground-900">{willPush}</strong> 位即将到期客户，涉及{" "}
              <strong className="text-foreground-900">{currentWindow.licenses}</strong> 张 {pushWindow} 天内到期的许可证。
              {currentWindow.skipped > 0
                ? `（命中 ${currentWindow.customers} 位，其中 ${currentWindow.skipped} 位本周期内已提醒将跳过）`
                : ""}
            </p>

            <div className="mt-2 flex flex-col gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-[11px] leading-relaxed text-foreground-500">
              <p className="flex items-start gap-1.5">
                <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center text-accent-600">
                  <i className="ri-shield-check-line" />
                </span>
                <span>
                  去重保护：同一客户在 {pushWindow} 天周期内只推一次，反复点击不会产生重复通知。
                </span>
              </p>
              <p className="flex items-start gap-1.5">
                <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center text-primary-600">
                  <i className="ri-mail-check-line" />
                </span>
                <span>
                  提醒同时写入客户通知中心（顶栏铃铛）并尝试发送账号邮箱邮件；未注册的游客申请不在推送范围内。
                </span>
              </p>
            </div>

            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                disabled={pushing}
                onClick={() => setConfirmPush(false)}
                className="inline-flex cursor-pointer items-center whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                取消
              </button>
              <button
                type="button"
                disabled={pushing || willPush === 0}
                onClick={handleConfirmPush}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-600 px-3.5 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-accent-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className={pushing ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} />
                </span>
                {pushing ? "推送中…" : willPush === 0 ? "本周期均已提醒" : `推送 ${willPush} 位`}
              </button>
            </div>
          </div>
        </div>
      )}

      {singleTarget && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-lg border border-background-200 bg-background-50 p-5">
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className="ri-send-plane-line" />
              </span>
              <h3 className="font-heading text-sm font-semibold text-foreground-950">单独推送续期提醒</h3>
            </div>

            <p className="mt-4 text-xs leading-relaxed text-foreground-600">
              将只向 <strong className="text-foreground-900">{singleTarget.customer.name}</strong> 补推一次续期提醒，涉及{" "}
              <strong className="text-foreground-900">{singleTarget.within30}</strong> 张 30 天内到期的许可证
              {singleTarget.next ? `，最近 ${formatFullDate(singleTarget.next)} 到期` : ""}。
            </p>

            <div className="mt-2 flex flex-col gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-[11px] leading-relaxed text-foreground-500">
              <p className="flex items-start gap-1.5">
                <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center text-accent-600">
                  <i className="ri-shield-check-line" />
                </span>
                <span>服务端仍复用周期去重：该客户若已在 30 天周期内被提醒过，本次会自动跳过，不会产生重复通知。</span>
              </p>
              <p className="flex items-start gap-1.5">
                <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center text-primary-600">
                  <i className="ri-mail-check-line" />
                </span>
                <span>提醒会写入该客户的站内通知，并尝试发送其账号邮箱邮件。</span>
              </p>
            </div>

            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                disabled={pushing}
                onClick={() => setSingleTarget(null)}
                className="inline-flex cursor-pointer items-center whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                取消
              </button>
              <button
                type="button"
                disabled={pushing}
                onClick={handleConfirmSinglePush}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-600 px-3.5 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-accent-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className={pushing ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} />
                </span>
                {pushing ? "推送中…" : "确认单独推送"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}